import React, { useState, useEffect } from 'react';
import { ConfigDocument, AlertSettings } from '../../types';

interface AlertsTabProps {
  config: ConfigDocument | null;
  onSave: (config: ConfigDocument) => Promise<void>;
  loading: boolean;
}

const DEFAULT_SETTINGS: AlertSettings = {
  enabled: true,
  recipients: [],
  lookbackMinutes: 60,
  failureCountThreshold: 5,
  failureRateThreshold: 50,
  destinationChecks: true,
  fileExpirationDays: 7,
};

const PRESETS: Record<string, Partial<AlertSettings>> = {
  low: { lookbackMinutes: 120, failureCountThreshold: 10, failureRateThreshold: 75 },
  medium: { lookbackMinutes: 60, failureCountThreshold: 5, failureRateThreshold: 50 },
  high: { lookbackMinutes: 30, failureCountThreshold: 3, failureRateThreshold: 25 },
};

const AlertsTab: React.FC<AlertsTabProps> = ({ config, onSave, loading }) => {
  const [settings, setSettings] = useState<AlertSettings>(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [dirty, setDirty] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (config?.alertSettings) {
      setSettings({ ...DEFAULT_SETTINGS, ...config.alertSettings });
    }
    setDirty(false);
  }, [config]);

  const updateField = <K extends keyof AlertSettings>(field: K, value: AlertSettings[K]) => {
    setSettings(prev => ({ ...prev, [field]: value }));
    setDirty(true);
    setSaveMessage(null);
  };

  const addRecipient = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    if (settings.recipients.includes(email)) {
      setEmailError('This email is already in the list');
      return;
    }
    updateField('recipients', [...settings.recipients, email]);
    setNewEmail('');
    setEmailError('');
  };

  const removeRecipient = (email: string) => {
    updateField('recipients', settings.recipients.filter(r => r !== email));
  };

  const applyPreset = (level: string) => {
    const preset = PRESETS[level];
    if (preset) {
      setSettings(prev => ({ ...prev, ...preset }));
      setDirty(true);
      setSaveMessage(null);
    }
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      await onSave({ ...config, alertSettings: settings });
      setDirty(false);
      setSaveMessage({ type: 'success', text: 'Alert settings saved successfully.' });
    } catch (err: any) {
      setSaveMessage({ type: 'error', text: err.message || 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return <div className="text-gray-500 py-8 text-center">Loading alert settings...</div>;
  }

  return (
    <div className="space-y-6">
      {saveMessage && (
        <div className={`px-4 py-3 rounded-md text-sm ${
          saveMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`} role="alert">
          {saveMessage.text}
        </div>
      )}

      {/* Enable/Disable */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Health Check Alerts</h3>
            <p className="text-sm text-gray-500 mt-1">
              Automated checks run every 15 minutes to detect error patterns and test destination connectivity.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.enabled}
              onChange={e => updateField('enabled', e.target.checked)}
              className="sr-only peer"
              aria-label="Enable health check alerts"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            <span className="ml-3 text-sm font-medium text-gray-700">{settings.enabled ? 'Enabled' : 'Disabled'}</span>
          </label>
        </div>
      </div>

      {/* Recipients */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-1">Alert Recipients</h3>
        <p className="text-sm text-gray-500 mb-4">
          Email addresses that will receive health check alerts. Each address must confirm the SNS subscription before receiving alerts.
        </p>
        <div className="flex space-x-2 mb-3">
          <input
            type="email"
            value={newEmail}
            onChange={e => { setNewEmail(e.target.value); setEmailError(''); }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addRecipient(); } }}
            placeholder="email@example.com"
            className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            aria-label="New recipient email address"
          />
          <button
            onClick={addRecipient}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
          >
            Add
          </button>
        </div>
        {emailError && <p className="text-sm text-red-600 mb-2">{emailError}</p>}
        {settings.recipients.length === 0 ? (
          <p className="text-sm text-gray-400 italic">No recipients configured. The default SNS topic subscribers will still receive CloudWatch alarm alerts.</p>
        ) : (
          <ul className="space-y-2">
            {settings.recipients.map(email => (
              <li key={email} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2">
                <span className="text-sm text-gray-700">{email}</span>
                <button
                  onClick={() => removeRecipient(email)}
                  className="text-red-500 hover:text-red-700 text-sm"
                  aria-label={`Remove ${email}`}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Sensitivity Presets */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Sensitivity Presets</h3>
        <div className="flex space-x-3">
          {(['low', 'medium', 'high'] as const).map(level => (
            <button
              key={level}
              onClick={() => applyPreset(level)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 capitalize"
            >
              {level}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Low: tolerant (10 failures, 75% rate, 2hr window) · Medium: balanced (5 failures, 50% rate, 1hr) · High: sensitive (3 failures, 25% rate, 30min)
        </p>
      </div>

      {/* Threshold Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Error Detection Thresholds</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="lookback" className="block text-sm font-medium text-gray-700 mb-1">
              Lookback Window (minutes)
            </label>
            <input
              id="lookback"
              type="number"
              min={5}
              max={1440}
              value={settings.lookbackMinutes}
              onChange={e => updateField('lookbackMinutes', parseInt(e.target.value) || 60)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">How far back to look for failures</p>
          </div>
          <div>
            <label htmlFor="failCount" className="block text-sm font-medium text-gray-700 mb-1">
              Failure Count Threshold
            </label>
            <input
              id="failCount"
              type="number"
              min={1}
              max={100}
              value={settings.failureCountThreshold}
              onChange={e => updateField('failureCountThreshold', parseInt(e.target.value) || 5)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">Alert when a site exceeds this many failures</p>
          </div>
          <div>
            <label htmlFor="failRate" className="block text-sm font-medium text-gray-700 mb-1">
              Failure Rate Threshold (%)
            </label>
            <input
              id="failRate"
              type="number"
              min={1}
              max={100}
              value={settings.failureRateThreshold}
              onChange={e => updateField('failureRateThreshold', parseInt(e.target.value) || 50)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-400 mt-1">Alert when a site's failure rate exceeds this %</p>
          </div>
        </div>
      </div>

      {/* Destination Checks */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Destination Health Checks</h3>
            <p className="text-sm text-gray-500 mt-1">
              Test connectivity to all configured SMB shares and SharePoint sites every 15 minutes.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={settings.destinationChecks}
              onChange={e => updateField('destinationChecks', e.target.checked)}
              className="sr-only peer"
              aria-label="Enable destination health checks"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            <span className="ml-3 text-sm font-medium text-gray-700">{settings.destinationChecks ? 'Enabled' : 'Disabled'}</span>
          </label>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Alert Settings'}
        </button>
      </div>
    </div>
  );
};

export default AlertsTab;
